import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, LinearProgress, Alert, Skeleton
} from '@mui/material';
import {
  Gavel as OperationalIcon,
  Cloud as AzureGovIcon,
} from '@mui/icons-material';
import { get } from '../../../services/apiService';

const MUTED   = '#64748b';
const SURFACE = '#f1f5fb';

const PILLAR_META = {
  operationalCompliance: { label: 'Operational Compliance',  icon: OperationalIcon, color: '#1565c0' },
  azureGovCompliance:    { label: 'Azure Gov Compliance',    icon: AzureGovIcon,    color: '#6a1b9a' },
};

const PillarCard = ({ pillarKey, data, onClick }) => {
  const meta = PILLAR_META[pillarKey];
  const cr          = data?.avgCompletionRate    || 0;
  const totalF      = data?.totalFeatures        || 0;
  const completedF  = data?.completedFeatures    ?? null;
  const totalSP     = data?.totalStoryPoints     || 0;
  const completedSP = data?.completedStoryPoints || 0;
  const configured  = data?.configured !== false;
  const Icon        = meta.icon;

  return (
    <Card
      elevation={0}
      onClick={() => onClick(pillarKey)}
      role="button"
      tabIndex={0}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onClick(pillarKey)}
      sx={{
        height: '100%', borderRadius: '20px', cursor: 'pointer', overflow: 'hidden',
        border: '1.5px solid rgba(0,0,0,0.07)',
        transition: 'all 0.22s cubic-bezier(0.4,0,0.2,1)',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: `0 20px 48px rgba(0,0,0,0.12), 0 0 0 2px ${meta.color}50`,
        },
        '&:focus-visible': { outline: `3px solid ${meta.color}`, outlineOffset: 3 },
      }}
    >
      <Box sx={{ px: 2.5, py: 2, bgcolor: `${meta.color}0d`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: '12px',
          bgcolor: `${meta.color}1a`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon sx={{ color: meta.color, fontSize: 24 }} />
        </Box>
        <Typography variant="body1" fontWeight={600} color="#1a1a1a" lineHeight={1.35} sx={{ flex: 1 }}>
          {meta.label}
        </Typography>
      </Box>

      <CardContent sx={{ px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={1}>
            <Typography sx={{ fontSize: '2.25rem', fontWeight: 700, color: meta.color, lineHeight: 1 }}>
              {cr}%
            </Typography>
            <Typography variant="caption" color={MUTED} fontWeight={500}>
              {configured ? 'complete' : 'not configured'}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={cr}
            sx={{
              height: 8, borderRadius: 4,
              bgcolor: `${meta.color}18`,
              '& .MuiLinearProgress-bar': { bgcolor: meta.color, borderRadius: 4 },
            }}
          />
        </Box>

        <Box display="flex" gap={1.5}>
          <Box sx={{ flex: 1, px: 1.5, py: 1.25, borderRadius: '10px', bgcolor: SURFACE, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} color="#1a1a1a" lineHeight={1}>
              {completedF !== null ? completedF : totalF}
              {completedF !== null && (
                <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 400, color: MUTED }}>
                  /{totalF}
                </Typography>
              )}
            </Typography>
            <Typography variant="caption" color={MUTED}>features</Typography>
          </Box>
          <Box sx={{ flex: 1, px: 1.5, py: 1.25, borderRadius: '10px', bgcolor: SURFACE, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} color="#1a1a1a" lineHeight={1}>
              {completedSP}
              <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 400, color: MUTED }}>
                /{totalSP}
              </Typography>
            </Typography>
            <Typography variant="caption" color={MUTED}>story pts</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const H22026ProgressDashboard = ({ onPillarClick }) => {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await get('/api/compliance-h2-2026/dashboard');
        if (res.success) setData(res.data);
        else setError('Failed to load dashboard data');
      } catch (e) {
        setError(e.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Grid container spacing={3}>
        {['operationalCompliance', 'azureGovCompliance'].map(k => (
          <Grid item xs={12} sm={6} key={k}>
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '20px' }} />
          </Grid>
        ))}
      </Grid>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Grid container spacing={3}>
        {Object.entries(PILLAR_META).map(([key]) => (
          <Grid item xs={12} sm={6} key={key}>
            <PillarCard
              pillarKey={key}
              data={data?.[key]}
              onClick={onPillarClick}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default H22026ProgressDashboard;
